import type { PurchaseOrder } from '@/api/purchaseOrders';
import type { PrintLetterhead } from '@core/api/bookings';
import { formatDocumentDate } from '@core/format/dates';
import { formatMoney } from '@core/format/money';

import { documentClass, type PaperSize } from './paper';

/**
 * A purchase order on paper, for the supplier (spec §16): the shop, the order number and date, who
 * it is to, each design and size with how many and at what rate, the value before tax, and when it
 * is expected. A line with no agreed rate prints an em dash, never a zero.
 */
export function PurchaseOrderSheet({
  paper,
  order,
  letterhead,
}: {
  paper: PaperSize;
  order: PurchaseOrder;
  letterhead: PrintLetterhead | undefined;
}) {
  const symbol = letterhead?.currencySymbol ?? '₹';
  const money = (value: number | null) => (value === null ? '—' : `${symbol}${formatMoney(value)}`);
  const ended = order.status === 'Cancelled' || order.status === 'ClosedShort';

  return (
    <article id="printableCard" className={`${documentClass(paper)} relative px-7 py-6`}>
      {order.status === 'Cancelled' && (
        <p
          aria-label="Cancelled"
          className="pointer-events-none absolute inset-0 flex items-center justify-center text-[4em] font-bold tracking-[0.2em] text-[color:var(--doc-rule)] -rotate-12"
        >
          CANCELLED
        </p>
      )}

      <header className="flex items-start justify-between gap-6">
        <div className="min-w-0">
          <p className="text-[1.35em] leading-tight font-bold">
            {letterhead?.tenantName ?? order.branchName}
          </p>
          <p className="text-[0.85em]">{letterhead?.branchName ?? order.branchName}</p>
          {letterhead?.addressLines.map((line) => (
            <p key={line} className="text-[0.85em] text-[color:var(--doc-muted)]">
              {line}
            </p>
          ))}
          {letterhead?.phone && <p className="text-[0.85em]">{letterhead.phone}</p>}
          {letterhead?.gstin && <p className="text-[0.85em]">GSTIN {letterhead.gstin}</p>}
        </div>
        <div className="shrink-0 text-right">
          <p className="text-[1.05em] font-bold tracking-[0.16em] uppercase">Purchase order</p>
          <p className="font-mono text-[0.95em] font-semibold">{order.documentNumber}</p>
          <p className="text-[0.85em] text-[color:var(--doc-muted)]">
            {formatDocumentDate(order.orderDate)}
          </p>
        </div>
      </header>

      <section className="mt-5 grid grid-cols-2 gap-4 border-t border-[color:var(--doc-rule-strong)] pt-3">
        <div>
          <p className="text-[0.8em] tracking-[0.12em] text-[color:var(--doc-muted)] uppercase">
            To
          </p>
          <p className="font-semibold">{order.supplierName}</p>
          {order.supplierMobile && <p className="text-[0.85em]">{order.supplierMobile}</p>}
        </div>
        <div>
          <p className="text-[0.8em] tracking-[0.12em] text-[color:var(--doc-muted)] uppercase">
            Expected by
          </p>
          <p>{order.expectedOn ? formatDocumentDate(order.expectedOn) : '—'}</p>
        </div>
      </section>

      <table className="mt-4 w-full border-collapse text-[0.9em]">
        <thead>
          <tr className="border-b border-[color:var(--doc-rule-strong)] text-left text-[0.85em] text-[color:var(--doc-muted)]">
            <th className="py-1.5 pr-2 font-semibold">Design</th>
            <th className="py-1.5 pr-2 text-right font-semibold">Qty</th>
            <th className="py-1.5 pr-2 text-right font-semibold">Rate</th>
            <th className="py-1.5 text-right font-semibold">Amount</th>
          </tr>
        </thead>
        <tbody>
          {order.lines.map((line) => (
            <tr key={line.id} className="border-b border-[color:var(--doc-rule)] align-top">
              <td className="py-1.5 pr-2">
                <p className="font-semibold">{line.itemName}</p>
                <p className="text-[0.85em] text-[color:var(--doc-muted)]">
                  {line.itemCode} · {line.variantDisplayName}
                </p>
              </td>
              <td className="py-1.5 pr-2 text-right">{line.quantity}</td>
              <td className="py-1.5 pr-2 text-right">{money(line.rate)}</td>
              <td className="py-1.5 text-right">
                {money(line.rate === null ? null : line.rate * line.quantity)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-3 flex items-baseline justify-between border-y border-[color:var(--doc-rule-strong)] py-2">
        <span className="font-bold">
          {order.orderedQuantity} garment{order.orderedQuantity === 1 ? '' : 's'} · before tax
        </span>
        <span className="text-[1.15em] font-bold">{money(order.orderValue)}</span>
      </div>

      {order.notes && <p className="mt-3 text-[0.9em]">{order.notes}</p>}

      {ended && order.endReason && (
        <p className="mt-3 text-[0.9em] font-semibold">
          {order.status === 'Cancelled' ? 'Cancelled' : 'Closed short'}
          {order.endedOn ? ` on ${formatDocumentDate(order.endedOn)}` : ''} — {order.endReason}
        </p>
      )}

      <footer className="mt-10 flex justify-end text-[0.8em] text-[color:var(--doc-muted)]">
        <span className="border-t border-[color:var(--doc-rule)] pt-1">
          For {letterhead?.tenantName ?? order.branchName}
        </span>
      </footer>
    </article>
  );
}
